import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { RollPositionsInputs, Weekday } from './apg-copilot-domain';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Chip row summarising the committed questionnaire inputs. Clicking anywhere on the strip asks the
// orchestrator to re-open (expand) the questionnaire — mirrors the Hedge CoPilot input-summary
// (whole-strip click, pointer cursor). Dumb/presentational.
@Component({
    selector: 'ets-apg-copilot-breadcrumb-bar',
    templateUrl: './apg-copilot-breadcrumb-bar.component.html',
    styleUrls: ['./apg-copilot-breadcrumb-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgCopilotBreadcrumbBarComponent {
    @Input() inputs: RollPositionsInputs | null = null;

    // CR-20260831-004: the changed-vs-last-run key map (apg-copilot-param-diff grammar). The bar
    // renders the LIVE inputs, so a changed chip shows the new value with the yellow "does not
    // match the results" tint. Null/empty = clean.
    @Input() changedKeys: { [key: string]: boolean } | null = null;

    @Output() expand = new EventEmitter<void>();

    // A chip collapses several diff keys — any hit under its family flags it.
    get datesChanged(): boolean {
        return this.anyKey(['date.']);
    }

    get exclusionsChanged(): boolean {
        return this.anyKey(['exclusions.']);
    }

    get biasChanged(): boolean {
        return this.anyKey(['bias.']);
    }

    private anyKey(prefixes: string[]): boolean {
        if (!this.changedKeys) {
            return false;
        }
        for (const k in this.changedKeys) {
            if (!this.changedKeys.hasOwnProperty(k)) {
                continue;
            }
            for (let i = 0; i < prefixes.length; i++) {
                if (k.indexOf(prefixes[i]) === 0) {
                    return true;
                }
            }
        }
        return false;
    }

    private isValid(d: Date | null | undefined): boolean {
        return d instanceof Date && !isNaN(d.getTime());
    }

    private pad(n: number): string {
        return n < 10 ? '0' + n : '' + n;
    }

    private fmt(d: Date): string {
        return this.pad(d.getDate()) + '-' + MONTHS[d.getMonth()] + '-' + this.pad(d.getFullYear() % 100);
    }

    private fmtDow(d: Date): string {
        return DOW3[d.getDay()] + ' ' + this.fmt(d);
    }

    get datesChip(): string | null {
        if (!this.inputs) {
            return null;
        }
        const dt = this.inputs.date;
        if (dt.kind === 'single') {
            return this.isValid(dt.single) ? 'Dates · ' + this.fmtDow(dt.single as Date) : null;
        }
        if (this.isValid(dt.start) && this.isValid(dt.end)) {
            return 'Dates · ' + this.fmtDow(dt.start as Date) + ' – ' + this.fmtDow(dt.end as Date);
        }
        return null;
    }

    get exclusionsChip(): string | null {
        if (!this.inputs) {
            return null;
        }
        const parts: string[] = [];
        this.inputs.weekdayExclusions.forEach(w => parts.push(w));
        this.inputs.specificDateExclusions.forEach(d => {
            if (this.isValid(d)) {
                parts.push(this.fmtDow(d));
            }
        });
        if (parts.length === 0) {
            return null;
        }
        // CR-20260904-001 (BR-W4): a day the trader brought back must not be summarised as a
        // plain "Excluded · Mon" — that states the opposite of what the run was sent. With no
        // override the chip is byte-identical to before. Wording provisional (owner question O24).
        const lifted = this.overriddenDayLabels();
        const except = lifted.length > 0 ? ' (Except ' + lifted.join(', ') + ')' : '';
        return 'Excluded · ' + parts.join(', ') + except;
    }

    // The brought-back days, ascending, as "Mon 14-Sep-26". Only days whose weekday is actually
    // excluded — with that weekday's rule off there is nothing to make an exception to
    // (CR-20260915-002: any excluded weekday, not Mondays only).
    private overriddenDayLabels(): string[] {
        if (!this.inputs) {
            return [];
        }
        const excluded = this.inputs.weekdayExclusions || [];
        return (this.inputs.weekdayOverrides || [])
            .filter(d => this.isValid(d) && excluded.indexOf(DOW3[d.getDay()] as Weekday) >= 0)
            .sort((a, b) => a.getTime() - b.getTime())
            .map(d => this.fmtDow(d));
    }

    get biasChip(): string | null {
        if (!this.inputs || !this.inputs.bias.enabled || !this.inputs.bias.direction) {
            return null;
        }
        const dir = this.inputs.bias.direction;
        return 'Bias · ' + dir.charAt(0).toUpperCase() + dir.slice(1);
    }

    get hasAny(): boolean {
        return !!(this.datesChip || this.exclusionsChip || this.biasChip);
    }
}
